import { useState } from "react";
import { motion } from "framer-motion";
import { Gamepad2, Brain, ListChecks, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import QuizGame from "../components/games/QuizGame";
import MemoryMatch from "../components/games/MemoryMatch";
import StoryBuilder from "../components/games/StoryBuilder";

const GAMES = [
  {
    id: "quiz",
    label: "Mission X Scenarios",
    sub: "What Would You Do?",
    description: "Real situations. Real choices. Earn Hero Stones for ethical decision-making.",
    icon: Brain,
  },
  {
    id: "memory",
    label: "Anti-Bullying Memory Match",
    sub: "Match the values",
    description: "Flip and match the core values that defuse bullying. Lock pairs in fewer moves.",
    icon: Gamepad2,
  },
  {
    id: "story",
    label: "Threat Defuse Protocol",
    sub: "Drag the steps in order",
    description: "Sequence the 5 steps cyber-safety counselors actually use to handle an online attack.",
    icon: ListChecks,
  },
];

export default function GamingPage() {
  const [active, setActive] = useState("quiz");

  const renderGame = () => {
    if (active === "quiz") return <QuizGame />;
    if (active === "memory") return <MemoryMatch />;
    return <StoryBuilder />;
  };

  return (
    <div data-testid="gaming-page" className="min-h-screen bg-[#FAFAFA] bg-grain pt-20 sm:pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="back-to-landing"
          className="inline-flex items-center gap-2 text-sm text-[#1A1414]/60 hover:text-[#E60000] transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Back to the worlds
        </Link>

        <div className="grid lg:grid-cols-[1fr_auto] items-end gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-2">
              Pillar 01 / Webjaggs Arcade
            </p>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A1414] tracking-tighter text-balance">
              Train your <span className="text-[#E60000]">digital reflexes.</span>
            </h1>
            <p className="mt-3 text-base text-[#1A1414]/70 max-w-2xl">
              Three quick missions built on the Webjaggs Cyber-Safety Decision Engine.
              Bullying, scams, sextortion, group-chat pressure — practice the moves before
              you ever need them.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          {/* Game selector */}
          <aside className="space-y-3">
            {GAMES.map((g) => {
              const Icon = g.icon;
              const isActive = active === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => setActive(g.id)}
                  data-testid={`game-select-${g.id}`}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                    isActive
                      ? "bg-[#1A1414] text-white border-[#1A1414] shadow-arc-lg"
                      : "bg-white border-[#FF8080]/30 text-[#1A1414] hover:border-[#E60000] hover:-translate-y-0.5 shadow-arc"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                        isActive ? "bg-[#E60000]" : "bg-[#FF8080]/15 text-[#E60000]"
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
                        isActive ? "text-[#FF8080]" : "text-[#8B0000]"
                      }`}>
                        {g.sub}
                      </p>
                      <h3 className="font-display font-bold text-base leading-tight mt-0.5">
                        {g.label}
                      </h3>
                      <p className={`text-xs mt-1.5 leading-snug ${
                        isActive ? "text-white/70" : "text-[#1A1414]/60"
                      }`}>
                        {g.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </aside>

          {/* Active game */}
          <motion.section
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-[#FF8080]/25 shadow-arc"
            data-testid="active-game-frame"
          >
            {renderGame()}
          </motion.section>
        </div>
      </div>
    </div>
  );
}
